import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import { Spin } from "antd";
import {
  getApiBaseUrl,
  UPDATE_WASH_IN,
  ADD_ACTIVITY,
  LOGIN,
} from "../../Config/Config";

const EditWashinReason = ({ editObj }) => {
  const [editedReason, setEditedReason] = useState({
    Id: "",
    Reason: "",
  });
  const [loading, setLoading] = useState(false);
  const [askPassword, setAskPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState("");
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const [changes, setChanges] = useState({});

  useEffect(() => {
    if (editObj) {
      setEditedReason({
        Id: editObj.Id || "",
        Reason: editObj.Reason || "",
      });
    }
  }, [editObj]);

  const fieldNameMap = {
    BlockName: "BlockName",
    Description: "Description",
  };

  console.log(changes, "changes");
  const handleChange = (e) => {
    const { name, value } = e.target;

    setEditedReason((prev) => {
      const newForm = { ...prev, [name]: value };
      const originalValue = editObj?.[name] || "";

      const mappedName = fieldNameMap[name] || name; // convert to FilterType/Description

      if (originalValue !== value) {
        setChanges((prevChanges) => ({
          ...prevChanges,
          [`Old${mappedName}`]: originalValue,
          [`New${mappedName}`]: value,
        }));
      } else {
        setEditedReason((prevChanges) => {
          const updated = { ...prevChanges };
          delete updated[`Old${mappedName}`];
          delete updated[`New${mappedName}`];
          return updated;
        });
      }

      return newForm;
    });
  };

  const handleAuth = async () => {
    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setAuthLoading(true);
    setError("");

    try {
      if (!user?.UserName) {
        throw new Error("User name not found in session data.");
      }

      const payload = {
        username: user.UserName,
        password,
      };

      const res = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status) {
        setAskPassword(false);
        setPassword("");
        await performUpdate(); // Proceed with update
      } else {
        throw new Error(
          data.Message || "Invalid credentials. Please try again."
        );
      }
    } catch (err) {
      setError(err.message || "Unable to authenticate. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const performUpdate = async () => {
    const payload = {
      Id: editedReason.Id,
      Reason: editedReason.Reason,
      Category: "test", // Replace with actual category if dynamic
      UpdatedBy: user.UserID,
    };
    const finalNote = Object.entries(changes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(", ");

    let isSuccess = 0;

    try {
      setLoading(true);

      const response = await fetch(`${getApiBaseUrl()}${UPDATE_WASH_IN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data?.ResultData?.length && data.ResultData[0].Status === "Failure") {
        isSuccess = 0;
        await logActivity(
          isSuccess,
          `Wash-in Reason ${finalNote} update failed`
        );
        Swal.fire({
          icon: "error",
          title: "Error",
          text: data.ResultData[0].ResultMessage,
        });
      } else {
        isSuccess = 1;
        await logActivity(
          isSuccess,
          `Wash-in Reason ${finalNote} updated successfully`
        );
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Reason updated successfully.",
        }).then(() => {
          window.location.reload();
        });
      }
    } catch (error) {
      isSuccess = 0;
      await logActivity(isSuccess, `Wash-in Reason ${finalNote} update failed`);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to connect to server.",
      });
    } finally {
      setLoading(false);
      setAskPassword(false);
      setPassword("");
      setError("");
    }
  };

  const logActivity = async (isSuccess, notes) => {
    try {
      await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ActivityType: 2, // Assuming 2 represents "Update"
          PerformedBy: user?.UserID,
          PerformedOn: new Date().toISOString().slice(0, 19).replace("T", " "),
          Notes: notes,
          Location: "UpdateWashInReason",
          IsSucces: isSuccess,
        }),
      });
    } catch (err) {
      console.error("Failed to log activity:", err);
    }
  };

  const handleUpdateWashInReasonSubmit = async (e) => {
    e.preventDefault();

    if (!editedReason.Reason) {
      setError("Please fill in the Reason field.");
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fill in the Reason field.",
      });
      return;
    }

    if (!user?.UserID) {
      setError("Invalid user data. Please log in again.");
      Swal.fire({
        icon: "error",
        title: "User Data Error",
        text: "Invalid user data. Please log in again.",
      });
      return;
    }

    setAskPassword(true); // Trigger reauthentication
  };

  return (
    <>
      {(loading || authLoading) && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}
      <div
        className="offcanvas offcanvas-end"
        tabIndex="-1"
        id="EditWashInReasonOffcanvas"
        aria-labelledby="EditWashInReasonOffcanvasLabel"
      >
        <form onSubmit={handleUpdateWashInReasonSubmit}>
          <div className="offcanvas-header">
            <h5 id="EditWashInReasonOffcanvasLabel" className="me-4">
              Edit Wash-in Reason
            </h5>
            <div className="d-flex align-items-center ms-auto">
              <button
                type="submit"
                className="btn btn-sm btn-outline-success me-2"
                title="Save"
                disabled={askPassword}
              >
                <i className="fa fa-check fs-4"></i>
              </button>
              <button
                type="button"
                className="btn-close text-reset"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
                disabled={askPassword}
              ></button>
            </div>
          </div>
          <div className="offcanvas-body">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
            <div className="mb-3">
              <label htmlFor="editReason" className="form-label">
                Reason
              </label>
              <span className="text-danger ms-1">*</span>
              <input
                type="text"
                className="form-control"
                id="editReason"
                name="Reason"
                value={editedReason.Reason}
                onChange={handleChange}
                required
                disabled={askPassword}
              />
            </div>
            {askPassword && (
              <div className="mt-2">
                <div className="mb-3 w-lg-75 m-auto">
                  <label className="form-label">
                    Confirm Password <span className="text-danger"> *</span>
                  </label>
                  <div className="pass-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="pass-input form-control"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError("");
                      }}
                      onCopy={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      onCut={(e) => e.preventDefault()}
                      placeholder="Enter Password"
                    />
                    <span
                      className={`ti toggle-password ${
                        showPassword ? "ti-eye" : "ti-eye-off"
                      }`}
                      onClick={() => setShowPassword(!showPassword)}
                    ></span>
                  </div>
                </div>
                <button
                  className="btn btn-primary btn-sm me-2"
                  onClick={handleAuth}
                  disabled={authLoading || !password}
                >
                  {authLoading ? "Verifying..." : "Confirm"}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setAskPassword(false);
                    setPassword("");
                    setError("");
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

EditWashinReason.propTypes = {
  editObj: PropTypes.object,
};

export default EditWashinReason;
